// src/shared/components/modals/common/ActionLayer.tsx
// 작은 레이어 박스 공통 컴포넌트
// - 타이틀 중앙 정렬 + 오른쪽 X 닫기 아이콘
// - 드래그 가능
// - 메인 submit 버튼에 자동 포커스 (Enter로 즉시 실행)
// - 표준 버튼 스타일 내장

import React, { useEffect, useRef, useCallback, useState } from "react"
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Paper, PaperProps, ButtonProps, Box } from "@mui/material"
import { SxProps, Theme } from "@mui/material/styles"
import CloseIcon from "@mui/icons-material/Close"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import Draggable from "react-draggable"
import { dialogTitleStyle, dialogCloseButtonStyle } from "@shared/components/common/ModalStyles"
import { clampOverlayDragPosition } from "@shared/components/modals/overlayDragRules"

// ============= 표준 버튼 색상/사이즈 토큰 =============
/** 레이어 버튼 표준 색상 */
export const layerButtonColors = {
  /** 메인 액션 버튼 (확인, 생성, 저장 등) */
  primary: {
    backgroundColor: "#1890ff",
    color: "#fff",
    hoverBackgroundColor: "#1578d4",
  },
  /** 보조 액션 버튼 (생성 후 출력, 다음 등) */
  secondary: {
    backgroundColor: "#36b37e",
    color: "#fff",
    hoverBackgroundColor: "#2d9a6b",
  },
  /** 경고 액션 버튼 */
  warning: {
    backgroundColor: "#ff9800",
    color: "#fff",
    hoverBackgroundColor: "#e68900",
  },
  /** 취소/닫기 버튼 */
  cancel: {
    backgroundColor: "transparent",
    color: "#666",
    hoverBackgroundColor: "rgba(0, 0, 0, 0.04)",
    border: "1px solid #d9d9d9",
  },
  /** 위험 액션 버튼 (삭제 등) */
  danger: {
    backgroundColor: "#ff4d4f",
    color: "#fff",
    hoverBackgroundColor: "#d9363e",
  },
} as const

/** 레이어 버튼 표준 사이즈 */
export const layerButtonSizes = {
  small: {
    fontSize: "12px",
    padding: "4px 12px",
    minWidth: "60px",
    height: "28px",
  },
  smallMedium: {
    fontSize: "14px",
    padding: "6px 16px",
    minWidth: "84px",
    height: "36px",
  },
  mediumCompact: {
    fontSize: "16px",
    padding: "6px 16px",
    minWidth: "84px",
    height: "40px",
  },
  medium: {
    fontSize: "20px",
    padding: "6px 16px",
    minWidth: "80px",
    height: "46px",
  },
  large: {
    fontSize: "14px",
    padding: "8px 24px",
    minWidth: "100px",
    height: "36px",
  },
} as const

type LayerButtonColor = keyof typeof layerButtonColors
type LayerButtonSize = keyof typeof layerButtonSizes
type ActionLayerVariant = "default" | "success" | "info" | "warning" | "error"

export type ActionLayerToneVariant = ActionLayerVariant
export type SharedActionButtonColor = LayerButtonColor
export type SharedActionButtonSize = LayerButtonSize
export const sharedActionButtonWidths = {
  standard: "112px",
  wide: "128px",
} as const
export type SharedActionButtonWidth = keyof typeof sharedActionButtonWidths

const actionLayerVariantColor: Record<ActionLayerVariant, string | null> = {
  default: null,
  success: "#4caf50",
  info: "#2196f3",
  warning: "#ff9800",
  error: "#f44336",
}

export const getActionLayerVariantButtonSx = (variant: ActionLayerToneVariant) => {
  if (variant === "warning" || variant === "error") {
    return {
      minWidth: 80,
      height: 36,
      fontSize: 14,
      px: 2,
    }
  }

  return {}
}

const getActionLayerVariantIcon = (variant: ActionLayerVariant) => {
  switch (variant) {
    case "success":
      return <CheckCircleOutlineIcon sx={{ color: actionLayerVariantColor.success, fontSize: 24 }} />
    case "info":
      return <InfoOutlinedIcon sx={{ color: actionLayerVariantColor.info, fontSize: 24 }} />
    case "warning":
      return <WarningAmberOutlinedIcon sx={{ color: actionLayerVariantColor.warning, fontSize: 24 }} />
    case "error":
      return <ErrorOutlineIcon sx={{ color: actionLayerVariantColor.error, fontSize: 24 }} />
    default:
      return null
  }
}

/** 표준 레이어 버튼 sx 생성 헬퍼 */
export const getLayerButtonSx = (color: LayerButtonColor = "primary", size: LayerButtonSize = "medium") => {
  const c = layerButtonColors[color]
  const s = layerButtonSizes[size]
  return {
    backgroundColor: c.backgroundColor,
    color: c.color,
    fontSize: s.fontSize,
    padding: s.padding,
    minWidth: s.minWidth,
    height: s.height,
    borderRadius: "4px",
    textTransform: "none" as const,
    boxShadow: "none",
    fontWeight: 500,
    ...("border" in c ? { border: c.border } : {}),
    "&:hover": {
      backgroundColor: c.hoverBackgroundColor,
      boxShadow: "none",
    },
    "&:disabled": {
      opacity: 0.5,
    },
  }
}

export const getSharedActionButtonSx = (
  color: SharedActionButtonColor = "primary",
  size: SharedActionButtonSize = "medium",
  width: SharedActionButtonWidth = "standard"
) => {
  return {
    ...getLayerButtonSx(color, size),
    width: sharedActionButtonWidths[width],
    flex: "0 0 auto",
    whiteSpace: "nowrap" as const,
  }
}

// ============= ActionLayerButton 컴포넌트 =============
interface ActionLayerButtonProps extends Omit<ButtonProps, "color" | "size"> {
  /** 버튼 색상 종류 (기본값: "primary") */
  layerColor?: LayerButtonColor
  /** 버튼 크기 (기본값: "medium") */
  layerSize?: LayerButtonSize
}

/** 표준 레이어 버튼 — ActionLayer 내부에서 사용 */
export const ActionLayerButton: React.FC<ActionLayerButtonProps> = ({ layerColor = "primary", layerSize = "medium", sx, ...rest }) => {
  return <Button sx={{ ...getLayerButtonSx(layerColor, layerSize), ...sx }} {...rest} />
}

// ============= DraggablePaper =============
interface ActionLayerPaperProps extends PaperProps {
  /** 변경 시 드래그 위치를 (0,0)으로 리셋 */
  draggableResetKey?: React.Key
  paperRef?: React.Ref<HTMLDivElement>
}

const assignPaperRef = (ref: React.Ref<HTMLDivElement> | undefined, node: HTMLDivElement | null) => {
  if (!ref) {
    return
  }

  if (typeof ref === "function") {
    ref(node)
    return
  }

  ;(ref as React.MutableRefObject<HTMLDivElement | null>).current = node
}

const ActionLayerPaper: React.FC<ActionLayerPaperProps> = ({ draggableResetKey, paperRef, ...props }) => {
  const nodeRef = React.useRef<HTMLDivElement | null>(null)
  const [position, setPosition] = useState({ x: 0, y: 0 })

  const handlePaperRef = useCallback(
    (node: HTMLDivElement | null) => {
      nodeRef.current = node
      assignPaperRef(paperRef, node)
    },
    [paperRef]
  )

  // draggableResetKey 변경 시 드래그 위치 초기화
  useEffect(() => {
    setPosition({ x: 0, y: 0 })
  }, [draggableResetKey])

  return (
    <Draggable
      nodeRef={nodeRef as unknown as React.RefObject<HTMLElement>}
      handle=".action-layer-title"
      cancel={'[class*="MuiDialogContent-root"], .action-layer-close-button'}
      position={position}
      onDrag={(_, data) => {
        setPosition(previousPosition =>
          clampOverlayDragPosition({
            rect: nodeRef.current?.getBoundingClientRect() ?? null,
            currentPosition: previousPosition,
            nextPosition: { x: data.x, y: data.y },
            viewportWindow: nodeRef.current?.ownerDocument.defaultView,
          })
        )
      }}
    >
      <Paper ref={handlePaperRef} {...props} />
    </Draggable>
  )
}

// ============= ActionLayer Props =============
export interface ActionLayerProps {
  /** 열림 여부 */
  open: boolean
  /** 닫기 콜백 */
  onClose: () => void
  /** 바깥 영역 클릭으로 닫기 비활성화 여부 */
  disableBackdropClose?: boolean
  /** Esc 키로 닫기 비활성화 여부 */
  disableEscapeClose?: boolean
  /** 배경 영역 클릭/상호작용 허용 여부 */
  allowBackgroundInteraction?: boolean
  /** Dialog 자동 포커스 비활성화 여부 */
  disableAutoFocus?: boolean
  /** Dialog 포커스 복원 비활성화 여부 */
  disableRestoreFocus?: boolean
  /** 타이틀 (중앙 정렬) — 문자열 또는 ReactNode */
  title: React.ReactNode
  /** 본문 콘텐츠 */
  children: React.ReactNode
  /** 하단 액션 버튼 영역 — ActionLayerButton 사용 권장 */
  actions?: React.ReactNode
  /** 메인 submit 콜백 — Enter 키 연결 */
  onSubmit?: () => void
  /** submit 버튼 비활성화 여부 */
  submitDisabled?: boolean
  /** submit 버튼 레이블 (기본값: "Submit") */
  submitLabel?: string
  /** submit 버튼 색상 (기본값: "primary") */
  submitColor?: LayerButtonColor
  /** Dialog 최대 너비 (기본값: "xs") */
  maxWidth?: "xs" | "sm" | "md" | "lg"
  /** fullWidth 여부 (기본값: true) */
  fullWidth?: boolean
  /** DialogContent dividers 여부 (기본값: true) */
  dividers?: boolean
  /** DialogContent 커스텀 sx */
  contentSx?: object
  /** DialogTitle 커스텀 sx */
  titleSx?: SxProps<Theme>
  /** Dialog Paper 커스텀 sx */
  paperSx?: object
  /** Dialog Paper ref */
  paperRef?: React.Ref<HTMLDivElement>
  /** Dialog 루트 컨테이너 커스텀 sx (정렬/위치 override 시 사용) */
  dialogSx?: object
  /** 변경 시 드래그 위치를 (0,0)으로 리셋 */
  draggableResetKey?: React.Key
  /** 드래그 가능 여부 (기본값: true) */
  draggable?: boolean
  /** X 닫기 버튼 표시 여부 (기본값: true) */
  showCloseButton?: boolean
  /** MessageDialog 스타일 톤 (기본값: default) */
  variant?: ActionLayerVariant
  /** Dialog 포털 컨테이너 */
  container?: Element | null
  /** 닫힌 상태에서도 콘텐츠를 마운트하여 다음 열기 속도 개선 */
  keepMounted?: boolean
}

// ============= ActionLayer 컴포넌트 =============
/**
 * 작은 레이어 박스 공통 컴포넌트 (ActionLayer)
 *
 * 특징:
 * - 타이틀 중앙 정렬 + 오른쪽 X 닫기 아이콘
 * - 드래그 가능 (타이틀 바 핸들)
 * - onSubmit 지정 시 메인 submit 버튼에 자동 포커스 → Enter 키로 즉시 실행
 * - 표준 버튼 스타일 내장 (ActionLayerButton)
 *
 * @example
 * <ActionLayer open={open} onClose={close} title="오늘날짜 영수증 생성" onSubmit={handleCreate}>
 *   <Typography>본문 내용</Typography>
 * </ActionLayer>
 *
 * // 커스텀 actions 사용
 * <ActionLayer
 *   open={open}
 *   onClose={close}
 *   title="진료일 선택"
 *   actions={
 *     <>
 *       <ActionLayerButton layerColor="cancel" onClick={close}>닫기</ActionLayerButton>
 *       <ActionLayerButton onClick={handleSubmit}>확인</ActionLayerButton>
 *     </>
 *   }
 * >
 *   ...
 * </ActionLayer>
 */
const ActionLayerComponent: React.FC<ActionLayerProps> = ({
  open,
  onClose,
  disableBackdropClose = false,
  disableEscapeClose = false,
  allowBackgroundInteraction = false,
  disableAutoFocus = false,
  disableRestoreFocus = false,
  title,
  children,
  actions,
  onSubmit,
  submitDisabled = false,
  submitLabel = "Submit",
  submitColor = "primary",
  maxWidth = "xs",
  fullWidth = true,
  dividers = true,
  contentSx,
  titleSx,
  paperSx,
  paperRef,
  dialogSx,
  draggableResetKey,
  draggable = true,
  showCloseButton = true,
  variant = "default",
  container,
  keepMounted = false,
}) => {
  const submitButtonRef = useRef<HTMLButtonElement>(null)
  const titleId = React.useId()

  // 레이어 열릴 때 submit 버튼에 포커스
  useEffect(() => {
    if (open) {
      // Dialog 렌더링 후 포커스 이동 (MUI 애니메이션 대기)
      const timer = setTimeout(() => {
        submitButtonRef.current?.focus()
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [open])

  // Enter 키로 submit 실행
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== "Enter" || !onSubmit || submitDisabled || e.defaultPrevented) return

      const target = e.target as HTMLElement
      if (
        target.tagName === "TEXTAREA" ||
        target.isContentEditable ||
        target.closest("button, [role='button'], a[href]") ||
        target.closest('[role="combobox"]')
      )
        return

      e.preventDefault()
      onSubmit()
    },
    [onSubmit, submitDisabled]
  )

  // 기본 actions: onSubmit이 제공되면 Submit 버튼 자동 표시
  const defaultActions = onSubmit ? (
    <ActionLayerButton ref={submitButtonRef} onClick={onSubmit} disabled={submitDisabled} layerColor={submitColor}>
      {submitLabel}
    </ActionLayerButton>
  ) : null

  const variantColor = actionLayerVariantColor[variant]
  const variantIcon = getActionLayerVariantIcon(variant)
  const resolvedTitleSx = [
    dialogTitleStyle,
    {
      cursor: "move",
      ...(variantColor
        ? {
            padding: "12px 16px",
            minHeight: "unset",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 1,
            borderBottom: `2px solid ${variantColor}`,
            color: variantColor,
            fontWeight: 700,
            fontSize: "1.25rem",
            lineHeight: 1.6,
            pb: 2,
          }
        : {}),
    },
    titleSx,
  ] as SxProps<Theme>

  const handleDialogClose = useCallback(
    (_event: object, reason: string) => {
      if (disableBackdropClose && reason === "backdropClick") {
        return
      }
      if (disableEscapeClose && reason === "escapeKeyDown") {
        return
      }

      onClose()
    },
    [disableBackdropClose, disableEscapeClose, onClose]
  )

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      disableEscapeKeyDown={disableEscapeClose}
      container={container ?? undefined}
      keepMounted={keepMounted}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      hideBackdrop={allowBackgroundInteraction}
      disableEnforceFocus={allowBackgroundInteraction}
      disableAutoFocus={allowBackgroundInteraction || disableAutoFocus}
      disableRestoreFocus={allowBackgroundInteraction || disableRestoreFocus}
      sx={{
        ...(allowBackgroundInteraction ? { pointerEvents: "none" } : {}),
        ...dialogSx,
      }}
      PaperComponent={draggable ? ActionLayerPaper : undefined}
      PaperProps={
        variant !== "default"
          ? ({
              sx: {
                borderRadius: 2,
                minWidth: 400,
                pointerEvents: "auto",
                ...paperSx,
              },
              ...(paperRef ? { paperRef } : {}),
              ...(draggableResetKey !== undefined ? { draggableResetKey } : {}),
            } as object)
          : ({
              sx: {
                pointerEvents: "auto",
                ...paperSx,
              },
              ...(paperRef ? (draggable ? { paperRef } : { ref: paperRef }) : {}),
              ...(draggableResetKey !== undefined ? { draggableResetKey } : {}),
            } as object)
      }
      aria-labelledby={titleId}
      onKeyDown={handleKeyDown}
    >
      <DialogTitle id={titleId} className="action-layer-title" sx={resolvedTitleSx}>
        {variantColor ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {variantIcon}
            <Box component="span" sx={{ fontWeight: "bold", color: variantColor, fontSize: "1.25rem", lineHeight: 1.6 }}>
              {title}
            </Box>
          </Box>
        ) : (
          title
        )}
        {showCloseButton && (
          <IconButton aria-label="닫기" className="action-layer-close-button" onClick={onClose} sx={dialogCloseButtonStyle}>
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent
        dividers={dividers}
        sx={{
          px: 2,
          py: 1.5,
          ...(variant !== "default"
            ? {
                pt: 3,
                pb: 2,
              }
            : {}),
          ...contentSx,
        }}
      >
        {variantColor ? (
          <Box
            sx={{
              width: "100%",
              "& .MuiTypography-root": {
                lineHeight: 1.6,
                wordBreak: "break-word",
              },
            }}
          >
            {children}
          </Box>
        ) : (
          children
        )}
      </DialogContent>
      {(actions || defaultActions) && (
        <DialogActions
          sx={{
            px: 2,
            py: 1,
            ...(variant !== "default"
              ? {
                  p: 2,
                  pt: 1,
                }
              : {}),
          }}
        >
          {actions || defaultActions}
        </DialogActions>
      )}
    </Dialog>
  )
}

const ActionLayer = React.memo(ActionLayerComponent)

export default ActionLayer
